import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import Joi from 'joi';

import { useConfirmSignInMutation } from 'api/main';
import { notificationCreated } from 'reducers/appNotification';
import { validateSchema } from 'utils/helpers/schemas';
import { CognitoErrors } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import { ActionButton, CancelButton, CustomDialogTitle, CustomTextField } from 'components';
import { Dialog, DialogActions, DialogContent } from '@mui/material';

const schema = Joi.string().trim().length(6).pattern(/^\d+$/).required().messages({
    'string.empty': texts.CODE_REQUIRED,
    'string.length': texts.LE_CODE_N_EST_PAS_VALIDE,
    'string.pattern.base': texts.LE_CODE_N_EST_PAS_VALIDE,
});

const ConfirmSignInDialog = ({ open, onClose, onSuccess, externalLoader }) => {
    const dispatch = useDispatch();
    const [code, setCode] = useState('');
    const [codeError, setCodeError] = useState('');
    const submittingRef = useRef(false);
    const [confirmSignIn, { isLoading: confirmLoader, error, isSuccess }] =
        useConfirmSignInMutation();

    const handleChange = useCallback(e => {
        setCode(e.target.value);
    }, []);

    const handleSubmit = useCallback(() => {
        // a Cognito MFA session can only be used once:
        // a second call would fail and hide the success of the first one
        if (submittingRef.current || isSuccess) {
            return;
        }

        const { valid, newErrors } = validateSchema(schema, code);
        if (valid) {
            setCodeError('');
            submittingRef.current = true;
            confirmSignIn({ code }).finally(() => {
                submittingRef.current = false;
            });
        } else {
            setCodeError(newErrors['']);
        }
    }, [code, confirmSignIn, isSuccess]);

    const handleKeyDown = useCallback(
        e => {
            if (e.key === 'Enter') {
                handleSubmit();
            }
        },
        [handleSubmit]
    );

    useEffect(() => {
        if (error?.name === CognitoErrors.CODE_MISMATCH) {
            setCodeError(texts.LE_CODE_N_EST_PAS_VALIDE);
        } else if (error?.name === CognitoErrors.CODE_EXPIRED) {
            setCodeError(texts.CODE_EXPIRED_RETRY);
        } else if (error?.name === CognitoErrors.UNAUTHORIZED) {
            // show message asking to restart the process
            setCodeError(texts.SESSION_EXPIRED_RETRY);
        } else if (error) {
            // show generic error
            dispatch(notificationCreated('ERROR_GENERAL'));
        }
    }, [error, dispatch]);

    useEffect(() => {
        if (isSuccess) {
            onSuccess();
        }
    }, [isSuccess, onSuccess]);

    useEffect(() => {
        if (!open) {
            setCode('');
            setCodeError('');
        }
    }, [open]);

    return (
        <Dialog open={open}>
            <CustomDialogTitle
                title={texts.TWO_FACTOR_AUTHENTICATION_TITLE}
                handleClose={onClose}
            />
            <DialogContent>
                <CustomTextField
                    inputRef={input => input?.focus()}
                    label={texts.ENTER_CODE_FROM_SMS}
                    value={code}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    error={codeError}
                />
            </DialogContent>
            <DialogActions>
                <CancelButton disabled={confirmLoader || externalLoader} onClick={onClose}>
                    {texts.CANCEL}
                </CancelButton>
                <ActionButton
                    disabled={confirmLoader || externalLoader}
                    loading={confirmLoader || externalLoader}
                    onClick={handleSubmit}
                >
                    {texts.CONTINUER}
                </ActionButton>
            </DialogActions>
        </Dialog>
    );
};

ConfirmSignInDialog.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired,
    externalLoader: PropTypes.bool,
};

export default ConfirmSignInDialog;
